import { useEffect, useState } from 'react';
import { FormContainer } from 'react-hook-form-mui';

import { configApi } from '@treneo/features/config';
import { useSelectUserTimezone } from '@treneo/features/user';
import type { ISaveCurrentUserInfoRequest, ITimezoneModel } from '@treneo/models';
import { AvatarConsts, DEFAULT_TIMEZONE } from '@treneo/shared/constants';
import {
  EmailField,
  FormButtons,
  SaveCurrentUserInfoSchema,
  SelectField,
  TextField,
  useAppFormWithValidation,
  useAppWatch
} from '@treneo/shared/forms';
import { useAppTranslations } from '@treneo/shared/i18n';
import { logger } from '@treneo/shared/logger';
import { Colors, Spacing } from '@treneo/shared/themes';
import { AlertSuccess, Avatar, Box, styled } from '@treneo/shared/ui';
import { useBoolean } from '@treneo/shared/utils';

import { ColorPicker } from './color-picker';
import { formFieldNames } from './form-field-names';

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.medium};
`;

const AvatarContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
`;

type Props = {
  onSubmit: (formData: ISaveCurrentUserInfoRequest) => void;
  onCancel?: () => void;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  avatarColor?: string | null;
  timezone?: string | null;
  isSubmitSuccess?: boolean;
  isLoading?: boolean;
};

export const MyAccountForm = ({
  onSubmit,
  onCancel,
  email,
  firstName,
  lastName,
  displayName,
  avatarColor,
  timezone,
  isSubmitSuccess,
  isLoading = false
}: Readonly<Props>) => {
  const { t } = useAppTranslations();
  const preferencesTimezone = useSelectUserTimezone();

  const [timezones, setTimezones] = useState<ITimezoneModel[]>([]);
  const [isLoadingTimezones, setIsLoadingTimezones] = useState(true);
  const [timezonesError, setTimezonesError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTimezones = async () => {
      setIsLoadingTimezones(true);
      setTimezonesError(null);

      try {
        const result = await configApi.getTimezones();

        if (result.isSuccess && result.data) {
          setTimezones(result.data.timezones);
        } else {
          setTimezonesError(result.error ?? 'Failed to load timezones');
        }
      } catch (error) {
        logger.warn('Failed to load timezones:', error);
        setTimezonesError('Failed to load timezones');
      } finally {
        setIsLoadingTimezones(false);
      }
    };

    fetchTimezones();
  }, []);

  const formFieldDefaultValues = {
    [formFieldNames.email]: email ?? undefined,
    [formFieldNames.firstName]: firstName ?? undefined,
    [formFieldNames.lastName]: lastName ?? undefined,
    [formFieldNames.displayName]: displayName ?? undefined,
    [formFieldNames.avatarColor]: avatarColor ?? undefined,
    [formFieldNames.timezone]: timezone ?? preferencesTimezone ?? DEFAULT_TIMEZONE
  };

  const formContext = useAppFormWithValidation<ISaveCurrentUserInfoRequest>(
    SaveCurrentUserInfoSchema(),
    { defaultValues: formFieldDefaultValues }
  );

  const [colorPickerIsDisplayed, displayColorPicker, hideColorPicker] = useBoolean();

  const firstNameValue = useAppWatch({
    control: formContext.control,
    name: formFieldNames.firstName
  });
  const displayNameValue = useAppWatch({
    control: formContext.control,
    name: formFieldNames.displayName
  });
  const colorValue = useAppWatch({
    control: formContext.control,
    name: formFieldNames.avatarColor
  });

  const userDisplayName = firstNameValue ?? displayNameValue ?? AvatarConsts.fallbackUserName;

  const handleColorClick = (value: string) => {
    formContext.setValue(formFieldNames.avatarColor, value);
    hideColorPicker();
  };

  const handleOnSubmit = (formData: ISaveCurrentUserInfoRequest) => {
    formData.avatarColor = colorValue ?? undefined;
    onSubmit(formData);
  };

  const timezoneOptions = timezones.map((tz: ITimezoneModel) => ({
    id: tz.id,
    label: tz.label
  }));

  return (
    <FormContainer onSuccess={handleOnSubmit} formContext={formContext}>
      <Container>
        {isSubmitSuccess && <AlertSuccess text={t('myAccount.changesHaveBeenSaved')} />}

        {!colorPickerIsDisplayed ? (
          <>
            <EmailField name={formFieldNames.email} disabled label={t('myAccount.loginEmail')} />

            <TextField name={formFieldNames.firstName} label={t('myAccount.firstName')} required />

            <TextField name={formFieldNames.lastName} label={t('myAccount.lastName')} required />

            <TextField
              name={formFieldNames.displayName}
              label={t('myAccount.displayName')}
              required
            />

            {isLoadingTimezones ? (
              <div>{t('loadingTimezones')}</div>
            ) : timezonesError ? (
              <div style={{ color: 'red' }}>
                {t('errorLoadingTimezones')}: {timezonesError}
              </div>
            ) : (
              <SelectField
                name={formFieldNames.timezone}
                label={t('myAccount.timezone')}
                options={timezoneOptions}
                required
                slotProps={{
                  select: {
                    MenuProps: {
                      style: { zIndex: 1600 },
                      PaperProps: {
                        sx: {
                          maxHeight: 300,
                          zIndex: 1600,
                          border: `2px solid ${Colors.darkGrey}`,
                          borderRadius: '8px',
                          boxShadow:
                            '0 8px 24px rgba(62, 66, 71, 0.12), 0 4px 12px rgba(62, 66, 71, 0.08)',
                          backgroundColor: Colors.white
                        }
                      }
                    }
                  }
                }}
              />
            )}

            <AvatarContainer onClick={displayColorPicker}>
              <Avatar color={colorValue ?? ''} displayName={userDisplayName} shouldBiggerOnHover />
              <span>{t('myAccount.avatarColor')}</span>
            </AvatarContainer>
          </>
        ) : (
          <ColorPicker displayName={userDisplayName} onColorClick={handleColorClick} />
        )}

        <FormButtons
          onSubmit={() => {}}
          onCancel={onCancel}
          submitText={t('Save')}
          cancelText={t('Cancel')}
          showCancel={!!onCancel}
          isDisabled={!formContext.isValid || isLoading}
          isLoading={isLoading}
          loadingText={t('common.saving')}
        />
      </Container>
    </FormContainer>
  );
};
